import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from 'shared/AppContext';
import {
  ModalBackdrop,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  SearchBox,
  SearchIcon,
  SearchInput,
  EventsList,
  MonthGroup,
  MonthLabel,
  EventItem,
  EventName,
  EventDate,
  ModalFooter,
  Button,
  LoadingContainer,
  EmptyState,
} from './StyledComponents';

const GET_EVENTS_BY_MONTH = gql`
  query GetEventsByMonth($input: GetEventsByMonthInput!) {
    getEventsByMonth(input: $input) {
      month
      events {
        id
        name
        start_date
        end_date
        times {
          start_time
        }
        isMultiDay
      }
    }
  }
`;

const EventSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedEventId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const modalRef = useRef(null);
  const { state } = useContext(AppContext);

  // Get account ID from context
  const accountId = state?.account?.id || state?.accountId;

  // Query for events by month
  const { data, loading, error } = useQuery(GET_EVENTS_BY_MONTH, {
    variables: {
      input: {
        account_id: accountId,
      },
    },
    skip: !isOpen || !accountId,
    fetchPolicy: 'network-only',
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Set initial selected event
  useEffect(() => {
    if (selectedEventId && data?.getEventsByMonth) {
      for (const monthGroup of data.getEventsByMonth) {
        const event = monthGroup.events.find((e) => e.id === selectedEventId);
        if (event) {
          setSelectedEvent(event);
          break;
        }
      }
    }
  }, [selectedEventId, data]);

  if (!isOpen) return null;

  // Filter events by search term
  const filterEventsByMonth = (monthGroups) => {
    if (!monthGroups) return [];

    return monthGroups
      .map((monthGroup) => {
        const filteredEvents = monthGroup.events.filter((event) => {
          if (!searchTerm) return true;
          const name = event.name?.toLowerCase() || '';
          const search = searchTerm.toLowerCase();
          return name.includes(search);
        });

        return {
          ...monthGroup,
          events: filteredEvents,
        };
      })
      .filter((group) => group.events.length > 0);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
  };

  const handleConfirm = () => {
    if (selectedEvent) {
      onSelect(selectedEvent);
      onClose();
    }
  };

  const formatEventDate = (event) => {
    const date = new Date(event.start_date);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const monthDay = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });

    // Check if event has a valid start time
    let timeStr = null;
    if (event.times && event.times.length > 0 && event.times[0].start_time) {
      const time = new Date(event.times[0].start_time);
      if (!isNaN(time.getTime())) {
        timeStr = time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        });
      }
    }

    // If no valid time from times array, check if start_date has time component
    if (!timeStr) {
      const dateHasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
      if (dateHasTime) {
        timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        });
      }
    }

    // Format for multi-day events
    if (event.isMultiDay) {
      const endDate = new Date(event.end_date);
      const endDay = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      if (timeStr) {
        return `${monthDay} - ${endDay} • ${timeStr}`;
      } else {
        return `${monthDay} - ${endDay}`;
      }
    }

    // Format for single-day events
    if (timeStr) {
      return `${dayName}, ${monthDay} • ${timeStr}`;
    } else {
      return `${dayName}, ${monthDay}`;
    }
  };

  const monthGroups = data?.getEventsByMonth || [];
  const filteredMonthGroups = filterEventsByMonth(monthGroups);

  return ReactDOM.createPortal(
    <ModalBackdrop>
      <ModalContainer ref={modalRef}>
        <ModalHeader>
          <ModalTitle>Select Event</ModalTitle>
          <SearchBox>
            <SearchIcon>
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search events by name or date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
        </ModalHeader>

        <EventsList>
          {loading && (
            <LoadingContainer>
              <div>Loading events...</div>
            </LoadingContainer>
          )}

          {error && (
            <EmptyState>
              <div>Error loading events. Please try again.</div>
            </EmptyState>
          )}

          {!loading && !error && filteredMonthGroups.length === 0 && (
            <EmptyState>
              <div>No events found</div>
              {searchTerm && <div>Try adjusting your search</div>}
            </EmptyState>
          )}

          {!loading &&
            !error &&
            filteredMonthGroups.map((monthGroup) => (
              <MonthGroup key={monthGroup.month}>
                <MonthLabel>{monthGroup.month.toUpperCase()}</MonthLabel>
                {monthGroup.events.map((event) => (
                  <EventItem
                    key={event.id}
                    selected={selectedEvent?.id === event.id}
                    onClick={() => handleEventSelect(event)}
                  >
                    <EventName>{event.name}</EventName>
                    <EventDate>{formatEventDate(event)}</EventDate>
                  </EventItem>
                ))}
              </MonthGroup>
            ))}
        </EventsList>

        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button primary onClick={handleConfirm} disabled={!selectedEvent}>
            Select Event
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalBackdrop>,
    document.body
  );
};

EventSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedEventId: PropTypes.number,
};

export default EventSelectionModal;
